import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Link, useSearchParams } from "react-router-dom";
import {
  Loader2, ShoppingBag, ExternalLink, Sparkles, Heart, Filter,
  Flame, TrendingUp, Search, Award, ShieldCheck,
} from "lucide-react";

const CATEGORY_ICON = {
  Books: "📖", STEM: "🔬", "Art & Craft": "🎨", Toys: "🧸",
  Puzzles: "🧩", Music: "🎹", Sports: "🏸", "Life Skills": "🌱",
  "Board Games": "♟️",
};

const BADGE_META = {
  "bestseller": { label: "Bestseller", icon: Award, className: "bg-brand-orange-soft text-brand-orange" },
  "trending": { label: "Trending", icon: Flame, className: "bg-red-50 text-red-600" },
  "most-searched": { label: "Most Searched", icon: Search, className: "bg-blue-50 text-brand-blue" },
};

const BADGE_FILTERS = [
  { id: "all", label: "All picks", icon: Sparkles },
  { id: "bestseller", label: "Bestsellers", icon: Award },
  { id: "trending", label: "Trending now", icon: Flame },
  { id: "most-searched", label: "Most searched", icon: Search },
];

export default function Marketplace() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialBand = searchParams.get("age_band") || "";
  const [bands, setBands] = useState([]);
  const [cats, setCats] = useState([]);
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ affiliate_tag: "", donation_pct: 15 });
  const [band, setBand] = useState(initialBand);
  const [category, setCategory] = useState("");
  const [badge, setBadge] = useState("all");
  const [loading, setLoading] = useState(true);

  // Keep URL in sync with the age_band filter for shareable links
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (band) next.set("age_band", band);
    else next.delete("age_band");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [band]);

  useEffect(() => {
    Promise.all([
      api.get("/marketplace/age-bands"),
      api.get("/marketplace/categories"),
    ]).then(([b, c]) => {
      setBands(b.data);
      setCats(c.data);
    });
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (band) params.age_band = band;
    if (category) params.category = category;
    api.get("/marketplace/products", { params }).then((r) => {
      setItems(r.data.items);
      setMeta({ affiliate_tag: r.data.affiliate_tag, donation_pct: r.data.donation_pct });
      setLoading(false);
    });
  }, [band, category]);

  const filtered = useMemo(() => {
    if (badge === "all") return items;
    return items.filter((p) => (p.badges || []).includes(badge));
  }, [items, badge]);

  const counts = useMemo(() => ({
    all: items.length,
    bestseller: items.filter((p) => (p.badges || []).includes("bestseller")).length,
    trending: items.filter((p) => (p.badges || []).includes("trending")).length,
    "most-searched": items.filter((p) => (p.badges || []).includes("most-searched")).length,
  }), [items]);

  const trackClick = (p) => {
    api.post("/affiliate/click", { product_id: p.id }).catch(() => {});
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      {/* HERO */}
      <div className="max-w-3xl mb-8">
        <div className="overline mb-3 flex items-center gap-2">
          <Sparkles size={12} /> Marketplace · Curated shortlist
        </div>
        <h1 data-testid="marketplace-heading" className="text-4xl sm:text-5xl font-semibold text-brand-blue leading-tight">
          Best-loved products, hand-picked by our team.
        </h1>
        <p className="mt-4 text-lg text-brand-muted">
          <strong>{items.length}+ curated picks</strong> across every age band — <em>books, STEM kits, art, music,
          sports, life-skills, and board games</em>. Chosen based on parent feedback,
          long-term popularity, and how well each item invites deep engagement — not fads.
        </p>
        <p className="mt-3 text-sm text-brand-muted">
          Every purchase supports our{" "}
          <Link to="/impact" className="text-brand-green font-medium underline">
            Learning for All
          </Link>{" "}
          impact pool — <strong>{meta.donation_pct}%</strong> of our affiliate commission funds learning programmes for children in orphanages.
        </p>
      </div>

      {/* Marketing strip */}
      <div className="grid sm:grid-cols-3 gap-3 mb-10">
        <div className="surface p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-brand-orange-soft text-brand-orange flex items-center justify-center"><Award size={18} /></div>
          <div className="text-sm">
            <div className="font-heading font-semibold text-brand-blue">Parent-tested picks</div>
            <div className="text-xs text-brand-muted">Loved by families across India</div>
          </div>
        </div>
        <div className="surface p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-brand-green-soft text-brand-green flex items-center justify-center"><TrendingUp size={18} /></div>
          <div className="text-sm">
            <div className="font-heading font-semibold text-brand-blue">Refreshed regularly</div>
            <div className="text-xs text-brand-muted">Trending picks updated seasonally</div>
          </div>
        </div>
        <div className="surface p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-50 text-brand-blue flex items-center justify-center"><ShieldCheck size={18} /></div>
          <div className="text-sm">
            <div className="font-heading font-semibold text-brand-blue">Independent choice</div>
            <div className="text-xs text-brand-muted">No paid rankings — ever</div>
          </div>
        </div>
      </div>

      {/* Impact banner */}
      <Link to="/impact" className="surface p-5 flex items-center gap-4 mb-10 bg-gradient-to-r from-brand-green-soft to-white border-brand-green/30">
        <div className="w-11 h-11 rounded-2xl bg-brand-green text-white flex items-center justify-center shrink-0"><Heart size={20} /></div>
        <div className="flex-1">
          <div className="text-sm font-semibold text-brand-blue">Every click gives back.</div>
          <div className="text-xs text-brand-muted">See how your shopping is funding learning for underprivileged children →</div>
        </div>
      </Link>

      {/* Badge tabs */}
      <div className="mb-6 overflow-x-auto">
        <div className="flex gap-2 min-w-max">
          {BADGE_FILTERS.map((b) => {
            const count = counts[b.id];
            const active = badge === b.id;
            return (
              <button
                key={b.id}
                data-testid={`marketplace-badge-${b.id}`}
                onClick={() => setBadge(b.id)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium border-2 transition-all ${
                  active
                    ? "bg-brand-blue text-white border-brand-blue"
                    : "bg-white text-brand-blue border-brand-border hover:border-brand-blue"
                }`}
              >
                <b.icon size={14} /> {b.label}
                <span className={`text-xs ${active ? "text-white/80" : "text-brand-muted"}`}>({count})</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3 mb-6">
        <div className="flex items-center gap-2 text-sm text-brand-muted shrink-0"><Filter size={16} /> Age band:</div>
        <div className="flex flex-wrap gap-2">
          <button data-testid="marketplace-band-all" onClick={() => setBand("")} className={`px-4 py-2 rounded-full text-sm font-medium border ${!band ? "bg-brand-blue text-white border-brand-blue" : "bg-white text-brand-blue border-brand-border"}`}>All ages</button>
          {bands.map((b) => (
            <button
              key={b.id}
              data-testid={`marketplace-band-${b.id}`}
              onClick={() => setBand(b.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium border ${band === b.id ? "bg-brand-blue text-white border-brand-blue" : "bg-white text-brand-blue border-brand-border"}`}
            >
              {b.label}
            </button>
          ))}
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mb-10">
        <button data-testid="marketplace-cat-all" onClick={() => setCategory("")} className={`chip ${!category ? "!bg-brand-blue !text-white" : ""}`}>All categories</button>
        {cats.map((c) => (
          <button key={c} data-testid={`marketplace-cat-${c.toLowerCase().replace(/\s+/g,'-').replace('&','and')}`} onClick={() => setCategory(c)} className={`chip ${category === c ? "!bg-brand-blue !text-white" : ""}`}>
            {CATEGORY_ICON[c] || "•"} {c}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-16 text-center"><Loader2 className="animate-spin mx-auto text-brand-green" /></div>
      ) : filtered.length === 0 ? (
        <p className="text-center text-brand-muted py-12">No products match this filter yet.</p>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((p) => (
            <div key={p.id} data-testid={`product-${p.id}`} className="surface surface-hover p-6 flex flex-col relative">
              {/* Badges */}
              {(p.badges || []).length > 0 && (
                <div className="absolute -top-3 left-4 flex gap-1.5 flex-wrap">
                  {p.badges.map((b) => {
                    const m = BADGE_META[b];
                    if (!m) return null;
                    return (
                      <span key={b} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold border border-white ${m.className}`}>
                        <m.icon size={11} /> {m.label}
                      </span>
                    );
                  })}
                </div>
              )}
              <div className="flex items-start justify-between mb-3 mt-2">
                <div className="w-12 h-12 rounded-2xl bg-brand-bg text-2xl flex items-center justify-center border border-brand-border">{CATEGORY_ICON[p.category] || "🎁"}</div>
                <span className="chip-blue">{p.category}</span>
              </div>
              <h3 className="text-lg font-semibold text-brand-blue leading-snug">{p.title}</h3>
              <p className="mt-2 text-sm text-brand-muted flex-1">{p.why}</p>
              <div className="mt-4 flex items-center justify-between">
                <span className="text-brand-blue font-heading font-medium">{p.price_range}</span>
                <span className="chip">Ages {p.age_band}</span>
              </div>
              <a
                href={p.url}
                target="_blank"
                rel="noopener noreferrer sponsored"
                data-testid={`product-buy-${p.id}`}
                onClick={() => trackClick(p)}
                className="btn-primary mt-5 w-full"
              >
                <ShoppingBag size={14} /> View on Amazon <ExternalLink size={12} />
              </a>
              <div className="mt-2 text-[10px] uppercase tracking-widest text-brand-muted text-center">
                Affiliate link · Supports Learning for All
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Full legal disclaimer */}
      <div className="mt-20 rounded-3xl bg-white border border-brand-border p-8">
        <div className="flex items-start gap-4">
          <ShieldCheck size={28} className="text-brand-blue shrink-0 mt-1" />
          <div className="text-sm text-brand-muted leading-relaxed space-y-3">
            <p className="font-semibold text-brand-blue text-base">Curation methodology &amp; disclaimer</p>
            <p>
              <strong>How we curate:</strong> The FuturePath team hand-picks each product based on a combination of long-term parent
              feedback, publicly available ratings, category popularity, and how well the product invites deep engagement
              (open-ended play, real skill-building, or lasting reading). Recommendations are refreshed periodically. Badges
              like <em>"Bestseller"</em>, <em>"Trending"</em> and <em>"Most Searched"</em> reflect our editorial view of general
              consumer interest and are <strong>not verified sales rankings</strong>, medical claims, or educational guarantees.
            </p>
            <p>
              <strong>No outcome guarantee:</strong> Every child is different. These are suggestions to help you narrow your
              choice — not prescriptions. A product's popularity does not guarantee it is right for your child. Always verify
              age suitability, safety warnings and manufacturer instructions before purchase and use.
            </p>
            <p>
              <strong>Independent editorial:</strong> No brand pays us to appear here. FuturePath does not accept paid product
              placement or sponsored rankings. Our only compensation is a standard Amazon affiliate commission on purchases you
              choose to make through our links — at no extra cost to you.
            </p>
            <p>
              <strong>Amazon Associates disclosure:</strong> FuturePath Learning (operated by Maanjil Bhuyan) is a participant
              in the Amazon Services LLC Associates Program (Amazon.in), an affiliate advertising programme designed to provide
              a means for sites to earn advertising fees by linking to Amazon. Prices, availability, product details and images
              on Amazon may change at any time; FuturePath does not control or guarantee them. Any transaction, warranty,
              refund, or delivery is strictly between you and Amazon (or the seller listed on Amazon).
            </p>
            <p>
              <strong>Learning for All commitment:</strong> {meta.donation_pct}% of our net affiliate commission is set aside for
              our Learning for All impact pool. Amounts shown on the <Link to="/impact" className="text-brand-green underline">Impact page</Link>{" "}
              are estimates based on average commission rates; actual figures are reconciled and reported periodically.
            </p>
            <p>
              <strong>Not professional advice:</strong> Content on this page is informational and not a substitute for
              professional educational, medical, psychological, legal, or financial advice. Consult qualified professionals for
              individual circumstances — especially for children with special needs.
            </p>
            <p className="text-xs">
              See our full <Link to="/terms" className="underline">Terms of Service</Link> and{" "}
              <Link to="/privacy" className="underline">Privacy Policy</Link> for the complete legal notice.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
